import type { BusinessProduct } from './businessData'
import {
  isProductFamily,
  publicProductTypeForFamily,
  type ProductFamily,
  type PublicProductType,
} from '../../shared/productFamily'
import {
  DEFAULT_PRODUCT_IMAGE_FRAMING,
  ORDER_PRODUCT_PRESENTATION_LIMITS,
  PRODUCT_PRESENTATION_LIMITS,
  defaultProductPresentation,
  normalizeHexColor,
  normalizeStoredProductImageFraming,
  validateProductImageFraming,
  type ProductImageFraming,
} from '../../shared/productPresentation'

type PublicMenuProduct = {
  id: string
  name: string
  productFamily: ProductFamily
  type: PublicProductType
  description: string
  image: string
  imageAlt?: string
  whatsInside: string[]
  approxWeightG: number | null
  ingredients: string
  allergens: string[]
  mayContainAllergens?: string[]
  gallery?: Array<{ src: string; alt: string; framing?: ProductImageFraming }>
  framing?: ProductImageFraming
  imageScale?: number
  imageOffsetX?: number
  imageOffsetY?: number
  orderImageScale?: number
  orderImageOffsetX?: number
  orderImageOffsetY?: number
  hoverBackground?: string
  availability: 'available' | 'sold-out'
  displayOrder: number
}

type PublicMenuPayload = {
  timeZone: 'Pacific/Auckland'
  week: { startsOn: string; endsOn: string; timeZone: 'Pacific/Auckland' }
  products: PublicMenuProduct[]
}

function validOptionalNumber(value: unknown, min: number, max: number) {
  return value === undefined || (typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max)
}

function validOptionalImageFraming(value: unknown) {
  if (value === undefined) return true
  try {
    validateProductImageFraming(value)
    return true
  } catch {
    return false
  }
}

function validProduct(value: unknown): value is PublicMenuProduct {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const product = value as Record<string, unknown>
  return typeof product.id === 'string'
    && typeof product.name === 'string'
    && isProductFamily(product.productFamily)
    && product.type === publicProductTypeForFamily(product.productFamily)
    && typeof product.description === 'string'
    && typeof product.image === 'string'
    && (product.imageAlt === undefined || typeof product.imageAlt === 'string')
    && Array.isArray(product.whatsInside)
    && product.whatsInside.every((item) => typeof item === 'string')
    && (product.approxWeightG === null || (typeof product.approxWeightG === 'number' && Number.isInteger(product.approxWeightG) && product.approxWeightG > 0))
    && typeof product.ingredients === 'string'
    && Array.isArray(product.allergens)
    && product.allergens.every((item) => typeof item === 'string')
    && (product.mayContainAllergens === undefined || (
      Array.isArray(product.mayContainAllergens)
      && product.mayContainAllergens.every((item) => typeof item === 'string')
    ))
    && (product.gallery === undefined || (
      Array.isArray(product.gallery)
      && product.gallery.every((item) => Boolean(item)
        && typeof item === 'object'
        && typeof (item as Record<string, unknown>).src === 'string'
        && typeof (item as Record<string, unknown>).alt === 'string'
        && validOptionalImageFraming((item as Record<string, unknown>).framing))
    ))
    && validOptionalImageFraming(product.framing)
    && validOptionalNumber(product.imageScale, PRODUCT_PRESENTATION_LIMITS.minScale, PRODUCT_PRESENTATION_LIMITS.maxScale)
    && validOptionalNumber(product.imageOffsetX, PRODUCT_PRESENTATION_LIMITS.minOffset, PRODUCT_PRESENTATION_LIMITS.maxOffset)
    && validOptionalNumber(product.imageOffsetY, PRODUCT_PRESENTATION_LIMITS.minOffset, PRODUCT_PRESENTATION_LIMITS.maxOffset)
    && validOptionalNumber(product.orderImageScale, ORDER_PRODUCT_PRESENTATION_LIMITS.minScale, ORDER_PRODUCT_PRESENTATION_LIMITS.maxScale)
    && validOptionalNumber(product.orderImageOffsetX, ORDER_PRODUCT_PRESENTATION_LIMITS.minOffset, ORDER_PRODUCT_PRESENTATION_LIMITS.maxOffset)
    && validOptionalNumber(product.orderImageOffsetY, ORDER_PRODUCT_PRESENTATION_LIMITS.minOffset, ORDER_PRODUCT_PRESENTATION_LIMITS.maxOffset)
    && (product.hoverBackground === undefined || normalizeHexColor(product.hoverBackground) !== null)
    && (product.availability === 'available' || product.availability === 'sold-out')
    && typeof product.displayOrder === 'number'
    && Number.isFinite(product.displayOrder)
}

function validPayload(value: unknown): value is PublicMenuPayload {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const payload = value as Record<string, unknown>
  const week = payload.week
  if (!week || typeof week !== 'object' || Array.isArray(week)) return false
  const weekRecord = week as Record<string, unknown>
  return payload.timeZone === 'Pacific/Auckland'
    && weekRecord.timeZone === 'Pacific/Auckland'
    && typeof weekRecord.startsOn === 'string'
    && typeof weekRecord.endsOn === 'string'
    && Array.isArray(payload.products)
    && payload.products.every(validProduct)
}

type DecoratedPublicMenuProduct = Omit<BusinessProduct, 'availability' | 'orderable'> & {
  imageAlt?: string
  availability: PublicMenuProduct['availability']
  orderable: boolean
}

function decorate(product: PublicMenuProduct): DecoratedPublicMenuProduct {
  const defaults = defaultProductPresentation(product.id, product.type)
  const framing = normalizeStoredProductImageFraming(product.framing, DEFAULT_PRODUCT_IMAGE_FRAMING)
  return {
    id: product.id,
    name: product.name,
    type: product.type,
    description: product.description,
    image: product.image,
    imageAlt: product.imageAlt,
    whatsInside: [...product.whatsInside],
    approxWeightG: product.approxWeightG,
    ingredients: product.ingredients,
    allergens: [...product.allergens],
    mayContainAllergens: [...(product.mayContainAllergens ?? [])],
    gallery: product.gallery?.map((item) => ({
      src: item.src,
      alt: item.alt,
      framing: normalizeStoredProductImageFraming(item.framing, DEFAULT_PRODUCT_IMAGE_FRAMING),
    })) ?? [{
      src: product.image,
      alt: product.imageAlt || product.name,
      framing,
    }],
    framing,
    enabled: true,
    displayOrder: product.displayOrder,
    isNew: false,
    clickTarget: '/order',
    hoverBackground: normalizeHexColor(product.hoverBackground) ?? defaults.hoverBackground,
    availability: product.availability,
    weeklyMenuIncluded: true,
    orderable: product.availability === 'available',
  }
}

export async function fetchPublishedMenu() {
  const response = await fetch('/api/menu', { credentials: 'same-origin', cache: 'no-store' })
  if (!response.ok) throw new Error('weekly-menu-unavailable')
  const payload = await response.json() as unknown
  if (!validPayload(payload)) throw new Error('weekly-menu-invalid')
  const products = payload.products.map(decorate).sort((a, b) => a.displayOrder - b.displayOrder)
  return { timeZone: payload.timeZone, week: payload.week, products }
}
